import { Clock } from 'lucide-react';
import { useId } from 'react';
import type { NetworkKinds, TopologyKind } from '../../api/types';
import { strings } from '../../strings';
import { kindReasonLabel, rovingKeyDown } from './helpers';

const l = strings.networks.lists;

/**
 * The topologies of the kinds registry as a radiogroup with roving focus (spec 11 6.15.3 step 4, 6.15.12 Topology):
 * the available ones as cards with their label and sentence, the others as compact rows with their reason badge,
 * `aria-disabled` and focusable, described by the reason.
 */
export function TopologyChoice({
  topologies,
  value,
  onChange,
  label,
  network = null,
  readOnly = false,
}: {
  topologies: NetworkKinds['topologies'];
  value: TopologyKind;
  onChange: (kind: TopologyKind) => void;
  label: string;
  network?: string | null;
  readOnly?: boolean;
}) {
  const ids = useId();
  const ordered = [
    ...topologies.filter((t) => t.status === 'available'),
    ...topologies.filter((t) => t.status !== 'available'),
  ];
  const radioId = (i: number) => `${ids}-radio-${i}`;
  const allIds = ordered.map((_, i) => radioId(i));
  const checkedIndex = ordered.findIndex((t) => t.id === value);
  const stop = checkedIndex >= 0 ? checkedIndex : 0;
  const choose = (i: number) => {
    const t = ordered[i];
    if (!readOnly && t && t.status === 'available') onChange(t.id);
  };
  return (
    <div
      role="radiogroup"
      aria-label={label}
      aria-readonly={readOnly || undefined}
      className="net-kind-group"
    >
      {ordered.map((t, i) => {
        const available = t.status === 'available';
        const reasonId = `${ids}-reason-${i}`;
        const sentence = l.topology[t.id];
        return (
          <div
            key={t.id}
            id={radioId(i)}
            role="radio"
            aria-checked={t.id === value}
            aria-disabled={!available || readOnly || undefined}
            aria-describedby={available ? `${ids}-text-${i}` : reasonId}
            tabIndex={i === stop ? 0 : -1}
            className={available ? 'net-kind-card' : 'net-kind-row'}
            onClick={() => choose(i)}
            onKeyDown={(event) => rovingKeyDown(event, allIds, i, choose)}
          >
            <span aria-hidden className="net-radio-dot" />
            <span className="min-w-0 flex-1">
              <span className="font-semibold">{t.label}</span>
              {available ? (
                <span id={`${ids}-text-${i}`} className="block text-sm text-fg-muted">
                  {sentence}
                </span>
              ) : (
                <span className="ml-2 text-sm">{sentence}</span>
              )}
            </span>
            {!available && (
              <span className="badge tone-neutral shrink-0">
                <Clock aria-hidden className="size-3.5" />
                {kindReasonLabel(t.status, null, network)}
              </span>
            )}
            {!available && (
              <span id={reasonId} className="sr-only">
                {t.reason?.text ?? kindReasonLabel(t.status, null, network)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
